import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, Lock, Eye, EyeOff, CheckCircle2, ArrowLeft } from 'lucide-react'
import { getErrorMessage } from '@/lib/utils'
import { authApi } from '../api/authApi'
import { supabaseAuth } from '../api/supabaseAuth'
import { useAuthStore } from '../stores/authStore'
import { useState, useEffect } from 'react'

const resetPasswordSchema = z.object({
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
})

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

export function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  // Two kinds of link arrive here.
  //
  // Legacy: the platform's own email carries ?e=&t= and the reset is completed against the API.
  // Supabase: the link carries its tokens in the URL FRAGMENT, which the client consumes on load
  // (detectSessionInUrl) to establish a session — so there are no query params to read, and the
  // presence of a session is what says the link was good.
  const email = searchParams.get('e') || ''
  const token = searchParams.get('t') || ''
  const hasLegacyParams = Boolean(email && token)

  const [supabaseSession, setSupabaseSession] = useState<boolean | null>(
    supabaseAuth.isConfigured() ? null : false,
  )

  useEffect(() => {
    if (!supabaseAuth.isConfigured()) return
    let cancelled = false
    // The session is established asynchronously from the fragment, so this cannot be read
    // synchronously on first render.
    supabaseAuth
      .currentAccessToken()
      .then((accessToken) => { if (!cancelled) setSupabaseSession(Boolean(accessToken)) })
      .catch(() => { if (!cancelled) setSupabaseSession(false) })
    return () => { cancelled = true }
  }, [])

  // Still resolving the fragment: showing "this link is invalid" here would be a lie told to
  // every Supabase user for the moment before their session lands.
  const isResolving = supabaseSession === null
  const hasValidParams = hasLegacyParams || supabaseSession === true

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const resetMutation = useMutation({
    mutationFn: async (password: string) => {
      // A Supabase session means this came from a Supabase link, and the reset completes against
      // that session. The legacy branch remains for links already sitting in artists' inboxes.
      if (supabaseSession === true) {
        const accessToken = await supabaseAuth.completePasswordReset(password)
        useAuthStore.getState().setToken(accessToken)
        return
      }
      return authApi.resetPassword(email, token, password)
    },
    onSuccess: () => {
      setIsSuccess(true)
    },
  })

  const onSubmit = (data: ResetPasswordFormData) => {
    resetMutation.mutate(data.password)
  }

  // Redirect to forgot password if missing params
  useEffect(() => {
    if (!hasValidParams) {
      // Show error state instead of auto-redirect to help users understand
    }
  }, [hasValidParams])

  // A Supabase link establishes its session from the URL fragment asynchronously. Rendering the
  // invalid-link state before that resolves would tell every one of those artists their link was
  // broken, in the instant before it works.
  if (isResolving && !hasLegacyParams) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-muted-foreground">Checking your reset link…</p>
      </div>
    )
  }

  // Invalid link state
  if (!hasValidParams) {
    return (
      <div className="w-full">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="font-serif text-4xl font-normal tracking-[0.2em] mb-4">Arthaus</h1>
          <h2 className="text-2xl font-semibold mb-2">Invalid Reset Link</h2>
          <p className="text-muted-foreground text-sm max-w-sm mx-auto">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
        </div>

        <div className="space-y-4">
          <Button
            className="w-full h-12"
            onClick={() => navigate('/forgot-password')}
          >
            Request New Reset Link
          </Button>

          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Success state - password changed
  if (isSuccess) {
    return (
      <div className="w-full">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="font-serif text-4xl font-normal tracking-[0.2em] mb-4">Arthaus</h1>
          <h2 className="text-2xl font-semibold mb-2">Password Changed!</h2>
          <p className="text-muted-foreground text-sm">
            Your password has been successfully updated. You can now sign in with your new password.
          </p>
        </div>

        <Button
          className="w-full h-12 text-base font-medium"
          onClick={() => navigate('/')}
        >
          Sign In
        </Button>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-serif text-4xl font-normal tracking-[0.2em] mb-4">Arthaus</h1>
        <h2 className="text-2xl font-semibold mb-2">Create New Password</h2>
        <p className="text-muted-foreground text-sm">
          Enter your new password below.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          {resetMutation.error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{getErrorMessage(resetMutation.error)}</AlertDescription>
            </Alert>
          )}

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-foreground">
                  New Password
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      {...field}
                      disabled={resetMutation.isPending}
                      className="pl-10 pr-10 h-12"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-foreground">
                  Confirm New Password
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      {...field}
                      disabled={resetMutation.isPending}
                      className="pl-10 pr-10 h-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Password requirements hint */}
          <div className="text-xs text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Password requirements:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>At least 8 characters</li>
              <li>One uppercase letter</li>
              <li>One lowercase letter</li>
              <li>One number</li>
            </ul>
          </div>

          <Button
            type="submit"
            className="w-full h-12 text-base font-medium"
            disabled={resetMutation.isPending}
          >
            {resetMutation.isPending ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Updating Password...
              </>
            ) : (
              'Set New Password'
            )}
          </Button>

          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to sign in
            </Link>
          </div>
        </form>
      </Form>
    </div>
  )
}
